import { Navigate, useLocation } from "react-router-dom"
import { ReactNode, useMemo } from "react"
import { useLoggedIn } from "../hooks/auth/useLoggedIn"
import useUser from "@/hooks/useUser"
import { rutaPermitida, rutasDelMenu, type NodoMenu } from "./menuRoutes"

function getRuntimeBasePath(pathname: string): string {
  const knownRootRoutes = new Set([
    "",
    "login",
    "inicio",
    "usuarios",
    "clientes",
    "portabilidad",
    "portabilidades",
    "consulta-uf",
    "batch",
    "preactivacion",
    "ventas",
    "menu",
    "ofertas",
    "datos-empresa",
    "msisdn",
    "facturacion",
    "liquidaciones",
    "ecommerce",
    "ecommerce-products",
    "esim-profiles",
    "empresas",
    "promociones-altan",
    "promociones",
    "banners",
    "sms-campaigns",
    // Sin esta entrada "/push-campaigns" se leía como prefijo de tenant: la ruta quedaba en "/",
    // el permiso fallaba y redirigía a "/push-campaigns/inicio" → 404 ("Avisos App" inaccesible).
    "push-campaigns",
    "medallas",
    "familias",
    "beneficios",
    "sms-templates",
    "perfil",
    "leads",
  ])
  const first = String(pathname || "").replace(/^\/+/, "").split("/")[0]?.toLowerCase() || ""
  if (!first || knownRootRoutes.has(first)) return ""
  return `/${first}`
}

function stripBase(pathname: string, basePath: string): string {
  if (!basePath) return pathname || "/"
  if (pathname === basePath) return "/"
  if (pathname.startsWith(`${basePath}/`)) return pathname.slice(basePath.length) || "/"
  return pathname || "/"
}

function withBase(path: string, basePath: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`
  return basePath ? `${basePath}${normalized}`.replace(/\/{2,}/g, "/") : normalized
}

const EsperandoPermisos = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Cargando…</span>
    </div>
  </div>
)

const MenuNoCargo = ({ onReintentar }: { onReintentar: () => void }) => (
  <div className="flex min-h-[60vh] items-center justify-center p-4">
    <div className="max-w-md text-center">
      <h1 className="mb-2 text-lg font-semibold text-foreground">No se pudo cargar tu menú</h1>
      <button type="button" onClick={onReintentar} className="text-sm font-medium text-primary hover:underline">
        Reintentar
      </button>
    </div>
  </div>
)

const SinPantallas = ({ onSalir }: { onSalir: () => void }) => (
  <div className="flex min-h-[60vh] items-center justify-center p-4">
    <div className="max-w-md text-center">
      <h1 className="mb-2 text-lg font-semibold text-foreground">Tu perfil todavía no tiene pantallas</h1>
      <p className="mb-0 text-sm text-muted-foreground">
        Pide a quien administra los perfiles de tu marca que te asigne permisos. En cuanto lo haga, se
        actualiza solo.
      </p>
      <button type="button" onClick={onSalir} className="mt-4 text-sm font-medium text-primary hover:underline">
        Cerrar sesión
      </button>
    </div>
  </div>
)

interface AuthProtectedProps {
  children: ReactNode
}

const AuthProtected: React.FC<AuthProtectedProps> = (props) => {
  const { loadingUserInfo: loading, isLoggedIn, closeSession } = useLoggedIn()
  const { user, isAdmin, menu, loadingUserInfo: loadingMenu, menuReady, menuError, retryMenu } = useUser()
  // Súper admin, dueño y dueño de tenant pasan todo el candado: aquí también, aunque su perfil les
  // dé un menú corto o vacío (si no, quedaban en "sin pantallas" teniendo acceso a todo).
  const u = user as { is_super_admin?: unknown; is_owner?: unknown; role?: unknown } | null
  const accesoTotal = !!(u?.is_super_admin || u?.is_owner || u?.role === "owner_tenant")
  // Un perfil de acceso completo (isAdmin) nunca es "sin pantallas" (el candado lo deja pasar a todo),
  // pero sí sigue su menú para las rutas: así no abre a mano pantallas sólo de súper admin.
  const location = useLocation()
  const basePath = getRuntimeBasePath(location.pathname)

  // Rutas a las que el menú del usuario deja entrar (ver menuRoutes.ts).
  const allowedUrls = useMemo(() => rutasDelMenu(menu as NodoMenu[]), [menu])

  // Obtener la primera URL permitida para redirección
  const firstAllowedUrl = useMemo(() => {
    return allowedUrls.length > 0 ? withBase(allowedUrls[0], basePath) : withBase("/login", basePath)
  }, [allowedUrls, basePath])

  // Verificar si el usuario tiene acceso a la ruta actual
  const hasRoutePermission = useMemo(() => {
    // Normalizar la ruta actual (quitar trailing slash excepto para root)
    const currentPathRaw = stripBase(location.pathname, basePath)
    const currentPath = currentPathRaw === "/"
      ? "/"
      : currentPathRaw.replace(/\/$/, "")

    // Si el menú aún no ha cargado o está cargando, permitir temporalmente para evitar loops
    if (!menu || !Array.isArray(menu) || loadingMenu) {
      return true
    }

    if (accesoTotal) return true

    // Igual o sub-ruta de una URL del menú (ej. /sms-campaigns/3, /empresas/12); /perfil siempre.
    return rutaPermitida(currentPath, allowedUrls)
  }, [location.pathname, allowedUrls, menu, loadingMenu, basePath, accesoTotal])

  // Mientras no se sepa qué pantallas tiene, NO se pinta la pantalla: pintarla disparaba sus
  // consultas (403 si no era suya) y luego redirigía. Ahora se espera con un indicador.
  if (isLoggedIn && menuError) {
    return <MenuNoCargo onReintentar={() => void retryMenu()} />
  }

  if (isLoggedIn && !menuReady) {
    return <EsperandoPermisos />
  }

  // Sesión válida pero el perfil no tiene ninguna pantalla: explicarlo en vez de rebotar.
  if (isLoggedIn && menuReady && !accesoTotal && !isAdmin && allowedUrls.length === 0 && stripBase(location.pathname, basePath) !== "/perfil") {
    return <SinPantallas onSalir={() => void closeSession()} />
  }

  if (loading || loadingMenu) {
    return <>{props.children}</>
  }

  // Redirigir a login si no está autenticado. `from` sale de `useLocation()` (pathname +
  // search), NUNCA de `props.location` — nadie se lo pasa (ProtectedLayout monta
  // `<AuthProtected>` sin ese prop), así que siempre era `undefined` y una liga directa a
  // "/ecommerce" en pestaña nueva perdía el destino: el login, sin a dónde volver, mandaba
  // siempre a "/" o "/inicio" (ver `useLoginForm`). `location.pathname` ya trae el prefijo de
  // tenant si existe (ej. "/pandamovil/ecommerce"): no hace falta tocarlo aquí.
  if (!isLoggedIn) {
    return (
      <Navigate
        to={withBase("/login", basePath)}
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    )
  }

  // Redirigir a la primera URL permitida si no tiene permiso para la ruta actual
  if (!hasRoutePermission) {
    console.warn(`Acceso denegado a ${location.pathname} - Redirigiendo a ${firstAllowedUrl}`)
    return (
      <Navigate
        to={firstAllowedUrl}
        replace
      />
    )
  }

  return <>{props.children}</>
}

export { AuthProtected }
